/*
  The words that more than one part of the app says about the same thing: the kinds of element, the kinds of line, and the
  parts of an item. One table each, so the engineer, the SME and the item page name a thing the same way (UI-25).
  Everyday engineering words (plain-words.md); every table is a Record over the contract's own set of values, so a
  new value in the contracts is a type error here.

  A part of the app that needs another form (a lower-case word inside a sentence) derives it from these, never from a
  second table.
*/

import type { ElementKind, ItemPart, LineKind } from "../api/types";

export const ELEMENT_KIND_WORDS: Record<ElementKind, string> = {
  item: "Item",
  family: "Family",
  sub_family: "Sub-family",
  library: "Library",
  component_kind: "Component kind",
  component: "Component",
  capability: "Capability",
  data_field: "Data field",
  data_group: "Data group",
  pick_list: "Pick list",
  document_kind: "Document kind",
  document_link: "Document link",
  link: "Link",
  spec_chapter: "Specification chapter",
  framework: "Framework element",
};

/** "pick_list" -> "Pick list": for a name the contracts give as a code. */
export function humanise(code: string): string {
  const text = code.replace(/[_-]+/g, " ").trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
}

/** The word for a kind of element. A kind a newer server adds keeps its own text, with the underscores taken out. */
export function elementKindWord(kind: string): string {
  return (ELEMENT_KIND_WORDS as Record<string, string>)[kind] ?? humanise(kind);
}

/** The same word inside a sentence: "a data field", "the sub-family". */
export function elementKindPhrase(kind: string): string {
  return elementKindWord(kind).toLowerCase();
}

export const LINE_KIND_WORDS: Record<LineKind, string> = {
  mapping: "Library mapping line",
  standards: "Standards line",
  phase_setting: "Required data",
  at_least_one: "At least one",
  pick_value: "Pick list value",
  symbol: "Symbol",
  line_style: "Line style",
};

export function lineKindWord(kind: string): string {
  return (LINE_KIND_WORDS as Record<string, string>)[kind] ?? humanise(kind);
}

export const ITEM_PART_WORDS: Record<ItemPart, string> = {
  own_fields: "The item's own data fields",
  components: "Components",
  tie_in_notes: "Tie-in notes",
  component_kinds: "Component kinds",
  units: "Units",
  data_groups: "Data groups",
  pick_lists: "Pick lists",
  capabilities: "Capabilities",
  connections: "Connections",
  links_and_place: "Links and place",
  document_links: "Document links",
  symbols: "Symbols",
  mapping_lines: "Library mapping lines",
  required_data: "Required data",
  chapters: "Chapters",
};

export const PART_STATUS_WORDS = {
  filled: "Filled",
  waiting_for_sme: "Waiting for the SME",
  not_started: "Not started",
  none_needed: "None needed",
} as const;

/*
  Plain sentences (UI-16). The server writes the notices, the history and the log from one table of plain words
  (`core/words.py`), so a sentence arrives in plain words. These helpers are the front end's own end of it: the same article
  rule for the sentences the app puts together itself, and a last net for a sentence that still holds a raw token of the
  contracts, begins in lower case, or names the agent without "the".
*/

/** "an item", "a data field": the article that goes with a word, by its first letter (the rule of `core/words.py`). */
export function withArticle(word: string): string {
  const text = word.trim();
  if (!text) return text;
  return `${"aeiou".includes(text.charAt(0).toLowerCase()) ? "an" : "a"} ${text}`;
}

/** The text with a capital first letter; nothing else changes (a name keeps its own case). */
export function sentence(text: string): string {
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
}

/** The tokens of the contracts that hold an underscore, and the words that say them: states, kinds and steps. */
const RAW_TOKEN_WORDS: Readonly<Record<string, string>> = {
  added_by_hand: "added by hand",
  next_version: "Next version",
  goal_and_item: "goal and item",
  hand_over: "hand-over",
  ...Object.fromEntries(Object.entries(ELEMENT_KIND_WORDS).filter(([kind]) => kind.includes("_")).map(([kind, word]) => [kind, word.toLowerCase()])),
  sent_back: "sent back",
};

const RAW_TOKENS = new RegExp(String.raw`\b(${Object.keys(RAW_TOKEN_WORDS).join("|")})\b`, "g");

/**
 * A notice, a history line or a log line in plain words: a raw token (`added_by_hand`, `sub_family`) is said in words, "agent"
 * or "app" at the start is "The agent" or "The app", and the first letter is a capital. A sentence that is plain already comes
 * back as it was.
 */
export function plainNotice(text: string | null | undefined): string {
  if (!text) return "";
  const words = text.trim().replace(RAW_TOKENS, (token) => RAW_TOKEN_WORDS[token] ?? token);
  // Only the lower-case word: a person called Agent Smith keeps the name.
  return sentence(words.replace(/^(?:[Tt]he\s+)?(agent|app)\b/, (_all, who: string) => `The ${who}`));
}
